import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { shopService } from '../../../services/artistContentService';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ShopSection = ({ artistData, onUpdate, isActive }) => {
  const { user } = useAuth();
  const [selectedProducts, setSelectedProducts] = useState({
    tshirts: artistData?.shop?.tshirts || true,
    posters: artistData?.shop?.posters || true,
    prints: artistData?.shop?.prints || true,
    bags: artistData?.shop?.bags || false,
    blankets: artistData?.shop?.blankets || false,
    sweatshirts: artistData?.shop?.sweatshirts || true,
    vinylAlbums: artistData?.shop?.vinylAlbums || false
  });

  const [printifyApiKey, setPrintifyApiKey] = useState(artistData?.shop?.printifyApiKey || '');
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [apiKeyValidation, setApiKeyValidation] = useState({ isValid: false, validated: false });

  // Load shop settings from Supabase on component mount
  useEffect(() => {
    const loadShopSettings = async () => {
      if (!user?.id) return;

      try {
        const { data: shopSettings, error } = await shopService?.getShopSettings(user?.id);
        
        if (error) {
          console.warn('Could not load shop settings:', error);
          return;
        }

        if (shopSettings) {
          // Update state with loaded settings
          if (shopSettings?.productTypes) {
            setSelectedProducts(shopSettings?.productTypes);
          }
          if (shopSettings?.printifyApiKey) {
            setPrintifyApiKey(shopSettings?.printifyApiKey);
          }
        }
      } catch (err) {
        console.error('Error loading shop settings:', err);
      }
    };

    loadShopSettings();
  }, [user?.id]);

  // Validate API key when it changes
  useEffect(() => {
    const validateApiKey = async () => {
      if (!printifyApiKey) {
        setApiKeyValidation({ isValid: false, validated: false });
        return;
      }

      try {
        const { isValid, error: validationError } = await shopService?.validatePrintifyApiKey(printifyApiKey);
        setApiKeyValidation({ 
          isValid, 
          validated: true, 
          error: validationError 
        });
      } catch (err) {
        console.error('API key validation error:', err);
        setApiKeyValidation({ 
          isValid: false, 
          validated: true, 
          error: 'Validation failed' 
        });
      }
    };

    const debounceTimer = setTimeout(validateApiKey, 1000); // Debounce validation
    return () => clearTimeout(debounceTimer);
  }, [printifyApiKey]);

  const productTypes = [
    {
      id: 'tshirts',
      name: 'T-Shirts',
      icon: 'Shirt',
      description: 'Custom printed t-shirts with your artwork',
      popular: true
    },
    {
      id: 'posters',
      name: 'Posters',
      icon: 'Image',
      description: 'High-quality poster prints of your work',
      popular: true
    },
    {
      id: 'prints',
      name: 'Art Prints',
      icon: 'FileImage',
      description: 'Premium art prints on various paper types',
      popular: true
    },
    {
      id: 'bags',
      name: 'Bags',
      icon: 'ShoppingBag',
      description: 'Tote bags and backpacks with your designs',
      popular: false
    },
    {
      id: 'blankets',
      name: 'Blankets',
      icon: 'Home',
      description: 'Cozy blankets featuring your artwork',
      popular: false
    },
    {
      id: 'sweatshirts',
      name: 'Sweatshirts',
      icon: 'Shirt',
      description: 'Comfortable sweatshirts with custom prints',
      popular: true
    },
    {
      id: 'vinylAlbums',
      name: 'Vinyl Albums',
      icon: 'Disc',
      description: 'Custom vinyl records (for music artists)',
      popular: false
    }
  ];

  const handleProductToggle = (productId) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: !prev?.[productId]
    }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(selectedProducts)?.every(selected => selected);
    const newState = {};
    productTypes?.forEach(product => {
      newState[product.id] = !allSelected;
    });
    setSelectedProducts(newState);
  };

  const handleSelectPopular = () => {
    const newState = { ...selectedProducts };
    productTypes?.forEach(product => {
      if (product?.popular) {
        newState[product.id] = true;
      }
    });
    setSelectedProducts(newState);
  };

  const handleSave = async () => {
    if (!user?.id) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    setError(null);
    setSaveSuccess(false);

    try {
      // Save shop settings to Supabase
      const shopData = {
        printifyApiKey: printifyApiKey,
        productTypes: selectedProducts
      };

      const { data, error: saveError } = await shopService?.saveShopSettings(user?.id, shopData);
      
      if (saveError) {
        throw new Error(saveError?.message || 'Failed to save shop settings');
      }

      // Update parent component if callback provided
      if (onUpdate) {
        onUpdate('shop', { 
          ...selectedProducts, 
          printifyApiKey: printifyApiKey 
        });
      }

      setSaveSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);

    } catch (err) {
      console.error('Error saving shop settings:', err);
      setError(err?.message || 'Failed to save shop settings');
    } finally {
      setLoading(false);
    }
  };

  const selectedCount = Object.values(selectedProducts)?.filter(Boolean)?.length;
  const totalCount = productTypes?.length;

  if (!isActive) return null;

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-destructive rounded-lg flex items-center justify-center">
            <Icon name="ShoppingCart" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Shop Products</h2>
            <p className="text-sm text-text-secondary">Select merchandise types to offer in your mobile app shop</p>
          </div>
        </div>
        
        <Button 
          variant="default" 
          size="sm" 
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-destructive" />
            <span className="text-sm text-destructive">{error}</span>
          </div>
        </div>
      )}

      {saveSuccess && (
        <div className="mb-4 p-3 bg-success/10 rounded-lg border border-success/20">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <span className="text-sm text-success">Shop settings saved successfully!</span>
          </div>
        </div>
      )}

      {/* Printify API Configuration */}
      <div className="mb-6 p-4 bg-muted rounded-lg border border-border">
        <div className="flex items-start space-x-3 mb-4">
          <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
            <Icon name="Key" size={16} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-text-primary">Printify API Configuration</h3>
            <p className="text-sm text-text-secondary">
              Connect your Printify account to enable print-on-demand fulfillment
            </p>
          </div>
        </div>
        
        <div className="max-w-md">
          <Input
            label="Printify API Key"
            type="password"
            placeholder="Enter your Printify API key"
            value={printifyApiKey}
            onChange={(e) => setPrintifyApiKey(e?.target?.value)}
            description="Your API key will be encrypted and stored securely in your profile settings"
          />
        </div>
        
        <div className="mt-3 flex items-center space-x-2 text-sm">
          <Icon 
            name={
              printifyApiKey && apiKeyValidation?.validated
                ? (apiKeyValidation?.isValid ? "CheckCircle" : "XCircle")
                : printifyApiKey 
                ? "Clock" :"AlertCircle"
            } 
            size={16} 
            className={
              printifyApiKey && apiKeyValidation?.validated
                ? (apiKeyValidation?.isValid ? "text-success" : "text-destructive")
                : printifyApiKey 
                ? "text-warning" :"text-warning"
            }
          />
          <span className={
            printifyApiKey && apiKeyValidation?.validated
              ? (apiKeyValidation?.isValid ? "text-success" : "text-destructive")
              : printifyApiKey 
              ? "text-warning" :"text-warning"
          }>
            {printifyApiKey && apiKeyValidation?.validated
              ? (apiKeyValidation?.isValid 
                  ? "API key is valid" 
                  : `API key validation failed: ${apiKeyValidation?.error || 'Invalid key'}`)
              : printifyApiKey 
              ? "Validating API key..." :"API key required for print-on-demand"}
          </span>
        </div>
        
        {!printifyApiKey && (
          <div className="mt-3 p-3 bg-warning/10 rounded-lg border border-warning/20">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={16} className="text-warning mt-0.5" />
              <div>
                <p className="text-sm font-medium text-warning">Setup Required</p>
                <p className="text-sm text-text-secondary">
                  Get your API key from your{' '}
                  <a 
                    href="https://printify.com/app/account/api" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-accent hover:underline"
                  >
                    Printify dashboard
                  </a>
                  {' '}to enable product fulfillment.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="flex items-center justify-between mb-6 p-4 bg-muted rounded-lg">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-text-primary">
            {selectedCount} of {totalCount} products selected
          </span>
          <div className="w-32 bg-border rounded-full h-2">
            <div 
              className="bg-accent h-2 rounded-full transition-all duration-300"
              style={{ width: `${(selectedCount / totalCount) * 100}%` }}
            />
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectPopular}
            iconName="Star"
            iconPosition="left"
            iconSize={16}
          >
            Select Popular
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectAll}
            iconName={Object.values(selectedProducts)?.every(selected => selected) ? "Square" : "CheckSquare"}
            iconPosition="left"
            iconSize={16}
          >
            {Object.values(selectedProducts)?.every(selected => selected) ? 'Deselect All' : 'Select All'}
          </Button>
        </div>
      </div>

      {/* Product Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {productTypes?.map((product) => (
          <div
            key={product?.id}
            className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
              selectedProducts?.[product?.id]
                ? 'border-accent bg-accent/5 shadow-md'
                : 'border-border hover:border-accent/50 hover:bg-muted/50'
            }`}
            onClick={() => handleProductToggle(product?.id)}
          >
            {/* Popular Badge */}
            {product?.popular && (
              <div className="absolute -top-2 -right-2 bg-warning text-warning-foreground text-xs px-2 py-1 rounded-full font-medium">
                Popular
              </div>
            )}
            
            <div className="flex items-start space-x-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                selectedProducts?.[product?.id] ? 'bg-accent' : 'bg-muted'
              }`}>
                <Icon 
                  name={product?.icon} 
                  size={20} 
                  color={selectedProducts?.[product?.id] ? 'white' : 'currentColor'}
                />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <h3 className="font-medium text-text-primary">{product?.name}</h3>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    selectedProducts?.[product?.id]
                      ? 'border-accent bg-accent' :'border-border'
                  }`}>
                    {selectedProducts?.[product?.id] && (
                      <Icon name="Check" size={12} color="white" />
                    )}
                  </div>
                </div>
                <p className="text-sm text-text-secondary">{product?.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Integration Info */}
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-accent mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-text-primary mb-1">Shop Integration</h4>
            <p className="text-sm text-text-secondary">
              Selected products will be available for purchase in your mobile app. 
              Print-on-demand fulfillment is handled automatically through Printify integration.
              All settings are securely stored in your profile and synced across your account.
            </p>
          </div>
        </div>
      </div>

      {/* Status Indicator */}
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-center space-x-2">
          <Icon 
            name={
              printifyApiKey && selectedCount > 0 ? "CheckCircle" : "AlertCircle"
            } 
            size={16} 
            className={
              printifyApiKey && selectedCount > 0 ? "text-success" : "text-warning"
            }
          />
          <span className="text-sm font-medium text-text-primary">
            Shop Status: {
              printifyApiKey && selectedCount > 0
                ? `Ready - ${selectedCount} product types with API configured`
                : printifyApiKey 
                ? `API configured - Select product types to enable shop`
                : selectedCount > 0 
                ? `${selectedCount} products selected - Configure API to enable`
                : "Setup required - Add API key and select products"
            }
          </span>
        </div>
        {(!printifyApiKey || selectedCount === 0) && (
          <p className="text-sm text-text-secondary mt-1">
            {!printifyApiKey && selectedCount === 0 
              ? "Configure your Printify API key and select at least one product type to enable the shop."
              : !printifyApiKey 
              ? "Add your Printify API key to enable print-on-demand fulfillment."
              : "Select at least one product type to enable the shop in your mobile app."
            }
          </p>
        )}
      </div>
    </div>
  );
};

export default ShopSection;